import React, { useEffect, useState } from 'react';
import {
  Box,
  Collapse,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import ShowChartOutlinedIcon from '@mui/icons-material/ShowChartOutlined';
import type { AppSettings } from '../../settings/types';

const ITEM_DEFAULT = '#c4ccd8';
const ITEM_ACTIVE = '#f97316';
const ITEM_ACTIVE_BG = 'rgba(249, 115, 22, 0.14)';

type Props = {
  pathname: string;
  settings: AppSettings;
  updateSettings: (patch: Partial<AppSettings> | ((prev: AppSettings) => AppSettings)) => void;
  navigate: (path: string) => void;
  onNavigate: () => void;
};

export function DudaNavGroup({ pathname, settings, updateSettings, navigate, onNavigate }: Props) {
  const { accounts, activeAccountId } = settings.integrations.duda;
  const [open, setOpen] = useState(() => pathname.startsWith('/duda'));

  useEffect(() => {
    if (pathname.startsWith('/duda')) {
      setOpen(true);
    }
  }, [pathname]);

  const parentSelected = pathname.startsWith('/duda');

  const setActiveAndGo = (accountId: string, path: string) => {
    updateSettings((prev) => ({
      ...prev,
      integrations: {
        ...prev.integrations,
        duda: { ...prev.integrations.duda, activeAccountId: accountId },
      },
    }));
    navigate(path);
    onNavigate();
  };

  const subStyles = (path: string, accountId: string) => {
    const matchPath =
      path === '/duda'
        ? pathname === '/duda' || pathname.startsWith('/duda/site/')
        : pathname === path || pathname.startsWith(`${path}/`);
    const selected = matchPath && activeAccountId === accountId;
    return {
      pl: 3,
      py: 0.75,
      borderRadius: 1.5,
      mb: 0.25,
      borderLeft: selected ? `3px solid ${ITEM_ACTIVE}` : '3px solid transparent',
      bgcolor: selected ? ITEM_ACTIVE_BG : 'transparent',
      color: selected ? ITEM_ACTIVE : ITEM_DEFAULT,
      '&:hover': {
        bgcolor: selected ? ITEM_ACTIVE_BG : 'rgba(148,163,184,0.08)',
      },
    };
  };

  return (
    <Box>
      <ListItemButton
        onClick={() => setOpen((v) => !v)}
        sx={{
          borderRadius: 1.5,
          py: 1,
          mb: 0.25,
          pl: 2,
          borderLeft: parentSelected ? `3px solid ${ITEM_ACTIVE}` : '3px solid transparent',
          bgcolor: parentSelected ? ITEM_ACTIVE_BG : 'transparent',
          color: parentSelected ? ITEM_ACTIVE : ITEM_DEFAULT,
          '&:hover': {
            bgcolor: parentSelected ? ITEM_ACTIVE_BG : 'rgba(148,163,184,0.08)',
          },
        }}
      >
        <ListItemIcon sx={{ color: parentSelected ? ITEM_ACTIVE : 'inherit', minWidth: 40 }}>
          <LanguageOutlinedIcon sx={{ fontSize: 22, color: '#38bdf8' }} />
        </ListItemIcon>
        <ListItemText
          primary="Website (Duda)"
          primaryTypographyProps={{ fontWeight: parentSelected ? 700 : 500, fontSize: '0.9rem' }}
        />
        {open ? <ExpandLess sx={{ color: ITEM_DEFAULT }} /> : <ExpandMore sx={{ color: ITEM_DEFAULT }} />}
      </ListItemButton>

      <Collapse in={open} timeout="auto" unmountOnExit>
        <Box sx={{ pl: 1, pr: 0.5, pb: 0.5 }}>
          {accounts.map((acc) => (
            <Box key={acc.id}>
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  px: 2,
                  py: 0.75,
                  color: '#64748b',
                  fontWeight: 700,
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                  fontSize: '0.6rem',
                }}
              >
                {acc.label || acc.id}
              </Typography>
              <ListItemButton
                onClick={() => setActiveAndGo(acc.id, '/duda')}
                sx={subStyles('/duda', acc.id)}
              >
                <ListItemIcon sx={{ minWidth: 36, color: 'inherit' }}>
                  <DashboardOutlinedIcon sx={{ fontSize: 18 }} />
                </ListItemIcon>
                <ListItemText primary="Sites & analytics" primaryTypographyProps={{ fontSize: '0.82rem' }} />
              </ListItemButton>
              <ListItemButton
                onClick={() => setActiveAndGo(acc.id, '/duda/analytics-cache')}
                sx={subStyles('/duda/analytics-cache', acc.id)}
              >
                <ListItemIcon sx={{ minWidth: 36, color: 'inherit' }}>
                  <ShowChartOutlinedIcon sx={{ fontSize: 18 }} />
                </ListItemIcon>
                <ListItemText primary="Analytics" primaryTypographyProps={{ fontSize: '0.82rem' }} />
              </ListItemButton>
            </Box>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
}
